import React from 'react';
import { ShieldCheck, Barcode as BarcodeIcon, QrCode, CheckCircle2, Lock, Clock, AlertTriangle } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { BarcodeViewer } from '../../../../shared/components/BarcodeViewer.js';
import { QrCodeViewer } from '../../../../shared/components/QrCodeViewer.js';

interface BookBarcodeQrSectionProps {
  book: Book;
  activeCodeTab: 'barcode' | 'qr';
  canEdit: boolean;
  onSelectTab: (tab: 'barcode' | 'qr') => void;
}

export const BookBarcodeQrSection: React.FC<BookBarcodeQrSectionProps> = ({
  book,
  activeCodeTab,
  canEdit,
  onSelectTab,
}) => {
  const isApproved = book.status === 'disetujui';

  // Checksum extraction
  const uibnParts = book.uibn.split('-');
  const checkDigit = uibnParts.length === 5 ? uibnParts[4] : '';
  const publisherCode = uibnParts.length === 5 ? uibnParts[2] : '';
  const sequenceNumber = uibnParts.length === 5 ? uibnParts[3] : '';

  if (!isApproved) {
    return (
      <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-200 shadow-2xs space-y-3">
        <div className="flex items-center justify-between gap-2 border-b border-slate-200/80 pb-2.5">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="font-bold text-slate-800 text-xs uppercase tracking-wide">
              Barcode 1D & QR Code 2D (Belum Tersedia)
            </span>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3 text-amber-600" />
            <span>Menunggu Persetujuan UIBN</span>
          </span>
        </div>

        <div className="p-4 rounded-xl bg-white border border-amber-200/80 flex flex-col items-center text-center space-y-2">
          <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-xs">
            <Lock className="w-6 h-6" />
          </div>
          <div className="max-w-md">
            <h4 className="font-bold text-slate-900 text-xs sm:text-sm">
              Aset Visual Barcode & QR Code Ditangguhkan
            </h4>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Sesuai ketentuan standar registrasi buku, berkas grafis <strong>Barcode 1D (Code128)</strong> dan <strong>QR Code 2D</strong> siap cetak belum disediakan selama masa pengajuan/kurasi. Seluruh kode visual dan tautan verifikasi publik akan aktif secara otomatis setelah UIBN disetujui resmi oleh Admin UIBN.
            </p>
          </div>

          <div className="pt-2 flex items-center gap-2 text-xs font-mono text-slate-700 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
            <span className="text-[10px] font-sans font-bold uppercase text-slate-400">Kode Dialokasikan:</span>
            <span className="font-bold text-indigo-700">{book.uibn}</span>
          </div>
        </div>

        {/* Code Breakdown Chips */}
        <div className="flex flex-wrap items-center gap-2 text-[10px] pt-1 text-slate-500">
          <span className="font-semibold text-slate-600">Alokasi Parameter:</span>
          <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 font-mono">
            Prefix: 979
          </span>
          <span className="px-2 py-0.5 rounded-md bg-white text-purple-700 border border-purple-200 font-mono">
            Penerbit: {publisherCode || '-'}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-white text-sky-700 border border-sky-200 font-mono">
            Nomor: {sequenceNumber || '-'}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-white text-emerald-700 border border-emerald-200 font-mono">
            Checksum: {checkDigit || '-'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-2xs space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="font-bold text-slate-800 text-xs uppercase tracking-wide">
            Status Barcode & QR Code Otentikasi
          </span>
        </div>

        {/* Toggle Tab Barcode / QR */}
        <div className="flex bg-slate-100 p-0.5 rounded-lg text-[11px] font-semibold w-full sm:w-auto">
          <button
            type="button"
            onClick={() => onSelectTab('barcode')}
            className={`flex-1 sm:flex-none px-3 py-1.5 sm:py-1 rounded-md transition cursor-pointer flex items-center justify-center gap-1.5 ${
              activeCodeTab === 'barcode'
                ? 'bg-white text-indigo-700 shadow-2xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <BarcodeIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">Barcode 1D (Code128)</span>
            <span className="sm:hidden">Barcode 1D</span>
          </button>
          <button
            type="button"
            onClick={() => onSelectTab('qr')}
            className={`flex-1 sm:flex-none px-3 py-1.5 sm:py-1 rounded-md transition cursor-pointer flex items-center justify-center gap-1.5 ${
              activeCodeTab === 'qr'
                ? 'bg-white text-indigo-700 shadow-2xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <QrCode className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">QR Code 2D (Mobile)</span>
            <span className="sm:hidden">QR Code 2D</span>
          </button>
        </div>
      </div>

      {/* Status Indicators Pill Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
        <div className="p-2 rounded-lg bg-emerald-50/70 border border-emerald-200/80 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <div>
            <div className="font-bold text-emerald-900">
              Barcode 1D: Ter-generate & Aktif
            </div>
            <div className="text-[10px] text-emerald-700">
              Standar Code128 • Modulo-10 Checksum (Digit: {checkDigit || '0'}) Valid
            </div>
          </div>
        </div>

        <div className="p-2 rounded-lg bg-indigo-50/70 border border-indigo-200/80 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
          <div>
            <div className="font-bold text-indigo-900">
              QR Code 2D: Ter-generate & Siap Pindai
            </div>
            <div className="text-[10px] text-indigo-700 truncate">
              Terkoneksi ke halaman verifikasi online 1-detik
            </div>
          </div>
        </div>
      </div>

      {/* Live Code Rendering Container */}
      <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col items-center justify-center">
        {activeCodeTab === 'barcode' ? (
          <div className="w-full flex flex-col items-center">
            <BarcodeViewer uibn={book.uibn} showDownload={canEdit} className="w-full max-w-sm" />
            <p className="text-[10px] text-slate-500 mt-1 text-center">
              Format Barcode 1D terstandarisasi untuk pencetakan sampul belakang (back cover) dan scanner retail POS.
            </p>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center">
            <QrCodeViewer uibn={book.uibn} bookTitle={book.title} size={130} showDownload={canEdit} className="w-full max-w-xs" />
            <p className="text-[10px] text-slate-500 mt-1 text-center">
              QR Code beresolusi tajam yang dapat dipindai oleh kamera ponsel pembaca untuk memvalidasi keaslian naskah.
            </p>
          </div>
        )}
      </div>

      {/* Code Breakdown Chips */}
      <div className="flex flex-wrap items-center gap-2 text-[10px] pt-1 border-t border-slate-100 text-slate-600">
        <span className="font-semibold text-slate-700">Struktur Kode:</span>
        <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 font-mono">
          Prefix: 979 (Standar Buku)
        </span>
        <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200 font-mono">
          Kode Penerbit: {publisherCode || '-'}
        </span>
        <span className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 font-mono">
          Nomor Urut: {sequenceNumber || '-'}
        </span>
        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono font-bold">
          Checksum: {checkDigit || '-'}
        </span>
      </div>
    </div>
  );
};
